import { useState, useEffect } from "react";

const useGoogleDrive = ({ clientId, apiKey, appId }) => {
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);
  const [pickerInited, setPickerInited] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);

  useEffect(() => {
    const loadGoogleAPIs = () => {
      // Load the Google API script
      const gapiScript = document.createElement("script");
      gapiScript.src = "https://apis.google.com/js/api.js";
      gapiScript.onload = initPicker;
      document.body.appendChild(gapiScript);

      // Load the Google Identity Services script
      const gisScript = document.createElement("script");
      gisScript.src = "https://accounts.google.com/gsi/client";
      gisScript.onload = initTokenClient;
      document.body.appendChild(gisScript);
    };

    loadGoogleAPIs();

    // Check for a stored access token and ensure it hasn't expired.
    const storedToken = localStorage.getItem("accessToken");
    const tokenExpiry = localStorage.getItem("tokenExpiry");
    if (storedToken && new Date().getTime() < tokenExpiry) {
      setGoogleAccessToken(storedToken);
      setIsGoogleAuthenticated(true);
    }

    return () => {
      // Cleanup: remove the scripts when the component unmounts.
      const googleScript = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
      const gisScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (googleScript) document.body.removeChild(googleScript);
      if (gisScript) document.body.removeChild(gisScript);
    };
  }, []);

  // Initialize the Google Picker API once gapi is loaded.
  const initPicker = () => {
    if (window.gapi) {
      window.gapi.load("picker", () => setPickerInited(true));
    }
  };

  // Initialize the OAuth token client using a read-only scope,
  // since we only need to let the user select files.
  const initTokenClient = () => {
    if (window.google && window.google.accounts && window.google.accounts.oauth2) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        // Use a read-only scope for Google Drive; this minimizes required permissions.
        scope: "https://www.googleapis.com/auth/drive.readonly",
        prompt: "consent", // Always prompt the user for consent
        callback: (response) => {
          if (response.error) {
            console.error("Error obtaining OAuth token:", response);
            return;
          }
          const expiresIn = response.expires_in * 1000;
          const expiryTime = new Date().getTime() + expiresIn;

          localStorage.setItem("accessToken", response.access_token);
          localStorage.setItem("tokenExpiry", expiryTime);

          setGoogleAccessToken(response.access_token);
          setIsGoogleAuthenticated(true);
        },
      });
      setTokenClient(client);
    } else {
      console.error("Google OAuth client library not available");
    }
  };

  // Request an access token silently.
  const silentAuthenticate = () => {
    if (!tokenClient) {
      console.error("Token client not initialized");
      return;
    }
    tokenClient.requestAccessToken();
  };

  // Create and display the Google Drive file picker.
  // The view is configured to include only files (folders are excluded).
  const createPicker = (pickerCallback) => {
    if (!pickerInited || !googleAccessToken) {
      console.error("Picker API or token not available");
      return;
    }

    const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
      .setIncludeFolders(false)       // Exclude folders from the view
      .setSelectFolderEnabled(false); // Disable folder selection

    const picker = new window.google.picker.PickerBuilder()
      .addView(docsView)
      .setOAuthToken(googleAccessToken)
      .setDeveloperKey(apiKey)
      .setAppId(appId)
      .setCallback(pickerCallback)
      .build();

    picker.setVisible(true);
  };

  return {
    isGoogleAuthenticated,
    createPicker,
    silentAuthenticate,
  };
};

export default useGoogleDrive;
